import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Plus,
  Pencil,
  X,
  Check,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { apiFetch } from '@/lib/apiFetch';

interface Skill {
  id: string;
  name: string;
  description: string | null;
  system_prompt: string;
  output_schema: Record<string, unknown> | null;
  retrieval_config: Record<string, unknown> | null;
  default_model_id: string | null;
  org_id: string | null;
  version: number;
  is_active: boolean;
  created_at: string;
}

interface AIModel {
  model_id: string;
  display_name: string;
}

const EMPTY_FORM = {
  name: '',
  description: '',
  system_prompt: '',
  output_schema: '',
  retrieval_config: '',
  default_model_id: '',
  scope: 'platform' as 'platform' | 'org',
};

interface FormPanelProps {
  editingId: string | null;
  form: typeof EMPTY_FORM;
  setForm: React.Dispatch<React.SetStateAction<typeof EMPTY_FORM>>;
  models: AIModel[];
  error: string | null;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
}

const FormPanel: React.FC<FormPanelProps> = ({
  editingId,
  form,
  setForm,
  models,
  error,
  saving,
  onSave,
  onCancel,
}) => (
  <Card className="mb-6">
    <CardHeader>
      <div className="flex items-center justify-between">
        <CardTitle className="text-base">
          {editingId ? `Edit Skill (new version will be created)` : 'New Skill'}
        </CardTitle>
        <Button variant="ghost" size="icon" onClick={onCancel}>
          <X className="w-4 h-4" />
        </Button>
      </div>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label>Name *</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            placeholder="Meeting Summarizer"
          />
        </div>
        <div className="space-y-1">
          <Label>Scope</Label>
          <select
            className="w-full border rounded px-3 py-1.5 text-sm"
            value={form.scope}
            onChange={(e) =>
              setForm((p) => ({
                ...p,
                scope: e.target.value as 'platform' | 'org',
              }))
            }
          >
            <option value="platform">Platform</option>
            <option value="org">My Organization</option>
          </select>
        </div>
      </div>
      <div className="space-y-1">
        <Label>Description</Label>
        <Input
          value={form.description}
          onChange={(e) =>
            setForm((p) => ({ ...p, description: e.target.value }))
          }
        />
      </div>
      <div className="space-y-1">
        <Label>System Prompt *</Label>
        <Textarea
          rows={6}
          className="font-mono text-xs"
          value={form.system_prompt}
          onChange={(e) =>
            setForm((p) => ({ ...p, system_prompt: e.target.value }))
          }
          placeholder="You are an expert…"
        />
      </div>
      <div className="space-y-1">
        <Label>Default Model</Label>
        <Select
          value={form.default_model_id}
          onValueChange={(v) => setForm((p) => ({ ...p, default_model_id: v }))}
        >
          <SelectTrigger>
            <SelectValue placeholder="Use tool config default" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="">— none —</SelectItem>
            {models
              .filter(
                (m) =>
                  m.model_id !== 'text-embedding-3-small' &&
                  m.model_id !== 'whisper-1'
              )
              .map((m) => (
                <SelectItem key={m.model_id} value={m.model_id}>
                  {m.display_name}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label>Output Schema (JSON, leave empty for free-form text)</Label>
        <Textarea
          rows={4}
          className="font-mono text-xs"
          value={form.output_schema}
          onChange={(e) =>
            setForm((p) => ({ ...p, output_schema: e.target.value }))
          }
          placeholder='{ "type": "object", "properties": { … } }'
        />
      </div>
      <div className="space-y-1">
        <Label>Retrieval Config (JSON, leave empty for no RAG)</Label>
        <Textarea
          rows={3}
          className="font-mono text-xs"
          value={form.retrieval_config}
          onChange={(e) =>
            setForm((p) => ({ ...p, retrieval_config: e.target.value }))
          }
          placeholder='{ "corpus": "county", "top_k": 8 }'
        />
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-2">
        <Button onClick={onSave} disabled={saving}>
          <Check className="w-4 h-4 mr-1" />
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </CardContent>
  </Card>
);

export const SkillsEditor: React.FC = () => {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [models, setModels] = useState<AIModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, mRes] = await Promise.all([
        apiFetch('/api/skills'),
        apiFetch('/api/ai/models'),
      ]);
      const sData = await sRes.json();
      const mData = await mRes.json();
      setSkills(sData?.data ?? []);
      setModels(mData?.data ?? []);
    } catch {
      /* silent */
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  const openCreate = () => {
    setForm({ ...EMPTY_FORM });
    setEditingId(null);
    setShowCreate(true);
    setError(null);
  };
  const openEdit = (s: Skill) => {
    setForm({
      name: s.name,
      description: s.description ?? '',
      system_prompt: s.system_prompt,
      output_schema: s.output_schema
        ? JSON.stringify(s.output_schema, null, 2)
        : '',
      retrieval_config: s.retrieval_config
        ? JSON.stringify(s.retrieval_config, null, 2)
        : '',
      default_model_id: s.default_model_id ?? '',
      scope: s.org_id ? 'org' : 'platform',
    });
    setEditingId(s.id);
    setShowCreate(false);
    setError(null);
  };

  const parseOptionalJson = (val: string): Record<string, unknown> | null => {
    if (!val.trim()) return null;
    return JSON.parse(val);
  };

  const save = async () => {
    setError(null);
    let output_schema: Record<string, unknown> | null = null;
    let retrieval_config: Record<string, unknown> | null = null;
    try {
      output_schema = parseOptionalJson(form.output_schema);
    } catch {
      setError('Output schema must be valid JSON or empty');
      return;
    }
    try {
      retrieval_config = parseOptionalJson(form.retrieval_config);
    } catch {
      setError('Retrieval config must be valid JSON or empty');
      return;
    }

    setSaving(true);
    try {
      const url = editingId ? `/api/skills/${editingId}` : '/api/skills';
      const method = editingId ? 'PATCH' : 'POST';
      const res = await apiFetch(url, {
        method,
        body: JSON.stringify({
          name: form.name,
          description: form.description,
          system_prompt: form.system_prompt,
          output_schema,
          retrieval_config,
          default_model_id: form.default_model_id || null,
          scope: form.scope,
        }),
      });
      if (!res.ok) {
        const d = await res.json();
        setError(d.error ?? 'Save failed');
        return;
      }
      setShowCreate(false);
      setEditingId(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    }
    setSaving(false);
  };

  // Group by name to show versions
  const grouped = skills.reduce<Record<string, Skill[]>>((acc, s) => {
    acc[s.name] = [...(acc[s.name] ?? []), s];
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Skills</h2>
        <Button size="sm" onClick={openCreate}>
          <Plus className="w-4 h-4 mr-1" />
          New Skill
        </Button>
      </div>

      {(showCreate || editingId) && (
        <FormPanel
          editingId={editingId}
          form={form}
          setForm={setForm}
          models={models}
          error={error}
          saving={saving}
          onSave={save}
          onCancel={() => {
            setShowCreate(false);
            setEditingId(null);
          }}
        />
      )}

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-10 bg-gray-100 animate-pulse rounded" />
          ))}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Latest ver.</TableHead>
              <TableHead>Model</TableHead>
              <TableHead>Retrieval</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead className="w-20" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {Object.keys(grouped).length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-center text-gray-400 py-8"
                >
                  No skills yet.
                </TableCell>
              </TableRow>
            )}
            {Object.entries(grouped).map(([name, versions]) => {
              const active = versions.find((v) => v.is_active) ?? versions[0];
              const isExpanded = expandedId === name;
              return (
                <React.Fragment key={name}>
                  <TableRow
                    className={editingId === active.id ? 'bg-blue-50' : ''}
                  >
                    <TableCell>
                      <button
                        className="flex items-center gap-1 font-medium text-sm"
                        onClick={() => setExpandedId(isExpanded ? null : name)}
                      >
                        {isExpanded ? (
                          <ChevronDown className="w-3 h-3" />
                        ) : (
                          <ChevronRight className="w-3 h-3" />
                        )}
                        {name}
                      </button>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">v{active.version}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">
                      {active.default_model_id ?? '—'}
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">
                      {active.retrieval_config ? '✓' : '—'}
                    </TableCell>
                    <TableCell>{active.org_id ? 'org' : 'platform'}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEdit(active)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                  {isExpanded &&
                    versions.map((v) => (
                      <TableRow key={v.id} className="bg-gray-50 text-sm">
                        <TableCell className="pl-8 text-gray-500">
                          v{v.version}{' '}
                          {v.is_active ? (
                            <Badge className="ml-1 text-xs py-0">active</Badge>
                          ) : (
                            <span className="text-xs text-gray-400 ml-1">
                              inactive
                            </span>
                          )}
                        </TableCell>
                        <TableCell
                          colSpan={5}
                          className="text-xs text-gray-400"
                        >
                          {new Date(v.created_at).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
};
